import * as THREE from 'three';

/**
 * `BufferGeometry.computeVertexNormals` for a deforming surface redrawn every frame: the same sums and normalisation in the same
 * order, through the arrays directly (so the same normals), without three's per-vertex reads and writes. Plain float attributes only;
 * anything else falls back to three's own.
 */
export function updateVertexNormals(geometry: THREE.BufferGeometry) {
  const position = geometry.getAttribute('position'), index = geometry.index;
  const normal = geometry.getAttribute('normal');
  if (!(position instanceof THREE.BufferAttribute) || !(normal instanceof THREE.BufferAttribute) || position.normalized || normal.normalized
    || !(position.array instanceof Float32Array) || !(normal.array instanceof Float32Array) || normal.count !== position.count || position.itemSize !== 3 || normal.itemSize !== 3) {
    geometry.computeVertexNormals(); return;
  }
  const p = position.array, n = normal.array;
  n.fill(0);
  const face = (a: number, b: number, c: number, add: boolean) => {
    const cbx = p[c] - p[b], cby = p[c + 1] - p[b + 1], cbz = p[c + 2] - p[b + 2], abx = p[a] - p[b], aby = p[a + 1] - p[b + 1], abz = p[a + 2] - p[b + 2];
    const x = cby * abz - cbz * aby, y = cbz * abx - cbx * abz, z = cbx * aby - cby * abx;
    // Three reads all three corners' sums before writing any (a repeated corner gains the face once) and stores each as a float.
    const ax = add ? n[a] + x : x, ay = add ? n[a + 1] + y : y, az = add ? n[a + 2] + z : z, bx = add ? n[b] + x : x, by = add ? n[b + 1] + y : y, bz = add ? n[b + 2] + z : z;
    const cx = add ? n[c] + x : x, cy = add ? n[c + 1] + y : y, cz = add ? n[c + 2] + z : z;
    n[a] = ax; n[a + 1] = ay; n[a + 2] = az; n[b] = bx; n[b + 1] = by; n[b + 2] = bz; n[c] = cx; n[c + 1] = cy; n[c + 2] = cz;
  };
  if (index) { const indices = index.array; for (let i = 0; i < index.count; i += 3) face(indices[i] * 3, indices[i + 1] * 3, indices[i + 2] * 3, true); }
  else for (let i = 0; i < position.count; i += 3) face(i * 3, i * 3 + 3, i * 3 + 6, false);
  for (let i = 0; i < n.length; i += 3) {
    const x = n[i], y = n[i + 1], z = n[i + 2], scale = 1 / (Math.sqrt(x * x + y * y + z * z) || 1);
    n[i] = x * scale; n[i + 1] = y * scale; n[i + 2] = z * scale;
  }
  normal.needsUpdate = true;
}
